// src/encounter/services/use-appointment-workflow.ts
// GET workflows/appointments/{appointmentId}/history → the appointment's care-pathway
// activity feed (PROD-738 Clinical Timeline). Flutter peer: `fetchAppointmentWorkflow`
// (encounter_data_source_impl.dart:1116-1119) — which is still a `return null` stub, so this
// is the first real caller on either platform.
//
// ⚠️ THE `/history` SUFFIX IS THE WHOLE POINT — see the header of `@/types/workflow-api`.
// The sibling route without it (`workflows/appointments/{id}`) is
// `RunAppointmentsWorkflowController`: it RUNS the workflow, firing
// CreateInvoiceForTheAppointment, FinalizeAppointment and OrderDefaultExaminationOrders.
// Both routes return the IDENTICAL shape, so pointing this at the run route would type-check,
// render a perfectly correct timeline, and quietly invoice and finalize every appointment a
// clinician opened. Never drop the suffix, and never give this query a `refetchInterval` —
// a polling read is exactly how that mistake would become a billing incident.
//
// Scrips.Workflows is a SEPARATE host from the PM-API, hence `getWorkflows`
// (env.workflowsBaseUrl in deployed builds, the Vite `/workflows` proxy locally) rather than
// the plain `get`. AbortSignal forwarded per project rule.
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { getWorkflows } from '@/shared/api/api-client';
import type { AppointmentWorkflowResponse } from '@/types/workflow-api';

export interface UseAppointmentWorkflowParams {
  appointmentId: string | null | undefined;
  /** Host-level override — false keeps the query idle (e.g. the caller was handed a feed). */
  enabled?: boolean;
}

export function useAppointmentWorkflow(
  params: UseAppointmentWorkflowParams,
): UseQueryResult<AppointmentWorkflowResponse> {
  const { appointmentId, enabled = true } = params;

  return useQuery<AppointmentWorkflowResponse>({
    queryKey: ['appointment-workflow', appointmentId],
    enabled: enabled && !!appointmentId,
    queryFn: ({ signal }) =>
      getWorkflows<AppointmentWorkflowResponse>(
        `/workflows/appointments/${encodeURIComponent(appointmentId as string)}/history`,
        { signal },
      ),
  });
}
